import React, { useEffect, useState } from 'react';
import {
  ComposedChart,
  Bar,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { FILTER_TYPES_KEYS } from '../../../constants/appraisal-comparison-filter-types';
import constants from '@shared-styles/constants.module.scss';
import NotEnoughDataMessage from '../../shared/components/NotEnoughDataMessage';
import { getXAxisLabel, getYAxisLabel } from '../../shared/components/GraphLabels';
import AggregatePercentageChartTooltip from './AggregatePercentageChartTooltip';

const chartNameKey = [
  { chartName: 'Industry Aggregates', dataKey: FILTER_TYPES_KEYS.Industry },
  { chartName: 'Organization Aggregates', dataKey: FILTER_TYPES_KEYS.Organization }
];

/**
 * @typedef {object} AggregatePercentageChartProps
 * @property {Array<*>} [data]
 * @property {string} [selectedDataKey]
 * @property {(data: import('recharts').BarRectangleItem, dataKey: string) => void} handleAggregatePercentageClick
 */

/**
 * @param {AggregatePercentageChartProps} props
 */
const AggregatePercentageChart = props => {
  const { data, selectedDataKey, handleAggregatePercentageClick } = props;
  const [displayUnitBar, setDisplayUnitBar] = useState(false);
  const [displayIndBar, setDisplayIndBar] = useState(false);
  const [displayOrgBar, setDisplayOrgBar] = useState(false);

  const resultsCount = data.map(a => (selectedDataKey == FILTER_TYPES_KEYS.Industry ? a.I : a.O));
  const total = resultsCount.reduce((a, b) => a + b, 0);

  useEffect(() => {
    if (selectedDataKey) {
      setDisplayIndBar(total >= 3 && selectedDataKey == chartNameKey[0].dataKey);
      setDisplayOrgBar(total >= 3 && selectedDataKey == chartNameKey[1].dataKey);
      setDisplayUnitBar(total >= 3);
    }
  }, [selectedDataKey, total]);

  return (
    <div onClick={e => e.stopPropagation()} data-testid='aggregate-percentage-chart'>
      {total <= 2 ? <NotEnoughDataMessage /> : null}
      <ResponsiveContainer width='95%' height={400}>
        <ComposedChart
          layout='vertical'
          data={data}
          margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: 40
          }}>
          <CartesianGrid stroke='#1D2D5C' strokeDasharray='2 2' strokeOpacity='.6' />
          <XAxis type='number' allowDecimals={false}>
            {getXAxisLabel('# Appraisals containing PA')}
          </XAxis>
          <YAxis dataKey='range' type='category' scale='band'>
            {getYAxisLabel('Aggregate PA % Distribution in Quintiles', 50, 20)}
          </YAxis>
          <Tooltip content={<AggregatePercentageChartTooltip />} />
          <Legend wrapperStyle={{ paddingTop: '1.5rem' }} align='center' />
          {displayIndBar && (
            <Bar
              name={chartNameKey[0].chartName}
              dataKey={chartNameKey[0].dataKey}
              barSize={20}
              fill={constants.industryColor}
              cursor='pointer'
              onClick={data => handleAggregatePercentageClick(data, chartNameKey[0].dataKey)}
            />
          )}
          {displayOrgBar && (
            <Bar
              name={chartNameKey[1].chartName}
              dataKey={chartNameKey[1].dataKey}
              barSize={20}
              fill={constants.organizationColor}
              cursor='pointer'
              onClick={data => handleAggregatePercentageClick(data, chartNameKey[1].dataKey)}
            />
          )}
          {displayUnitBar && (
            <Scatter
              dataKey={
                selectedDataKey == FILTER_TYPES_KEYS.Industry ? 'IndMidPoint' : 'OrgMidPoint'
              }
              name='Unit'
              legendType='circle'
              fill={constants.unitColor}
              stroke={constants.unitColor}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AggregatePercentageChart;
